<template>
  <div class="gi-root resizable-columns">

    <div class="gi-table-label">APPROVED INVOICES</div>

    <!-- Selection action bar -->
    <div class="gi-action-bar" v-if="modelValue.length">
      <span class="gi-action-bar__count">{{ modelValue.length }} selected</span>
      <div class="gi-action-bar__buttons">
        <b-button size="sm" variant="primary" @click="downloadSelected('xls')">↓ XLS</b-button>
        <b-button size="sm" variant="primary" @click="downloadSelected('pdf')">↓ PDF</b-button>
        <b-button
          v-if="allSelectedUndispatched"
          size="sm"
          variant="primary"
          :disabled="!!actionInFlight"
          @click="dispatchSelected"
        >
          <b-spinner v-if="actionInFlight === 'dispatch'" small class="mr-1" />
          Manual Dispatch
        </b-button>
        <b-button
          v-if="allSelectedUndispatched"
          size="sm"
          variant="primary"
          :disabled="!!actionInFlight"
          @click="returnSelected"
        >
          <b-spinner v-if="actionInFlight === 'return'" small class="mr-1" />
          Return to Draft
        </b-button>
        <b-button size="sm" variant="warning" :disabled="!!actionInFlight" @click="clearSelection">✕ Clear</b-button>
      </div>
    </div>

    <div v-if="activeFilterMode" class="gi-filter-banner">
      Showing:
      <strong>{{ filterBannerLabel }}</strong>
      <a href @click.prevent="clearFilterMode">Clear filter</a>
    </div>

    <div v-if="loadingInvoices" class="gi-loading">Loading approved/dispatched invoices…</div>
    <div v-else-if="loadError" class="gi-error">{{ loadError }}</div>
    <div v-else-if="invoices.length === 0" class="gi-empty">No approved or dispatched invoices found.</div>

    <BaseDataTable
      v-else
      v-model="modelValue"
      ref="dataTableRef"
      :columns="ApprovedDispatchedTableColumns"
      :items="itemsProvider"
      :total="total"
      :initial-page-size="100"
      :page-size-options="[100, 200, 500, 1000]"
      :sticky-actions-column="false"
      :is-row-selectable="isRowSelectable"
      selectable
    >
      <template v-slot:cell(invoice_ref)="{ item }">
        <span class="gi-ref">{{ item.invoice_reference }}</span>
      </template>

      <template v-slot:cell(billing_type)="{ item }">{{ item.billing_type || 'Market' }}</template>

      <template v-slot:cell(tpa)="{ item }">{{ item.tpa_name || '—' }}</template>
      <template v-slot:cell(underwriter)="{ item }">{{ item.underwriter_name || '—' }}</template>
      <template v-slot:cell(currency_type)="{ item }">{{ item.currency_type }}</template>

      <template v-slot:cell(bdx_count)="{ item }">
        <span class="gi-num">{{ item.bdx_count }}</span>
      </template>

      <template v-slot:cell(amount)="{ item }">
        <span class="gi-num gi-amount">{{ exportState.formatCurrency(item.amount, item.currency_type) }}</span>
      </template>

      <template v-slot:cell(billing_contact)="{ item }">
        <span class="gi-contact" v-if="exportState.billingContactList(item).length">
          {{ exportState.billingContactList(item)[0] }}
          <a
            v-if="exportState.billingContactList(item).length > 1"
            href
            class="gi-contact-more"
            @click.prevent="exportState.openBillingContactsModal(item)"
          >+{{ exportState.billingContactList(item).length - 1 }}</a>
        </span>
        <span class="gi-contact" v-else>—</span>
      </template>

      <template v-slot:cell(tpa_address)="{ item }">
        <span v-if="item.tpa_address" class="gi-contact gi-truncate-cell" :title="item.tpa_address">{{ firstAddressLine(item.tpa_address) }}</span>
        <span v-else-if="item.billing_type === 'LIMOSS'" class="gi-contact">—</span>
        <span v-else class="gi-alert-flag" title="TPA has no address on file">— missing</span>
      </template>

      <template v-slot:cell(filevine_id)="{ item }">
        <span v-if="item.tpa_filevine_id" class="gi-contact gi-truncate-cell" :title="item.tpa_filevine_id">{{ truncateFilevine(item.tpa_filevine_id) }}</span>
        <span v-else-if="item.billing_type === 'LIMOSS'" class="gi-contact">—</span>
        <span v-else class="gi-alert-flag" title="TPA has no FileVine Project Email on file">— missing</span>
      </template>

      <template v-slot:cell(status)="{ item }">
        <span class="gi-status-badge" :class="statusBadgeClass(item)">{{ statusLabel(item) }}</span>
        <b-button
          v-if="item.changed_after_approve"
          size="sm"
          variant="primary"
          class="gi-reapprove-btn"
          :disabled="reapprovingInvoiceId === item.invoice_id"
          @click="reapproveOne(item)"
        >
          <b-spinner v-if="reapprovingInvoiceId === item.invoice_id" small />
          <template v-else>Reapprove</template>
        </b-button>
      </template>

      <template v-slot:cell(dispatched_at)="{ item }">
        <span class="gi-num">{{ item.dispatched_at || '—' }}</span>
      </template>

    </BaseDataTable>

    <!-- Billing Contacts Modal -->
    <b-modal
      v-model="exportState.showBillingContactsModal.value"
      centered
      hide-header
      hide-footer
      size="sm"
      @hide="exportState.onBillingContactsModalHide"
    >
      <div class="bcm-root">
        <button class="bcm-close" @click="exportState.onBillingContactsModalHide" aria-label="Close">
          <b-icon icon="x" />
        </button>
        <div class="bcm-header">
          <div class="bcm-header__icon-wrap">
            <b-icon icon="envelope-fill" class="bcm-header__icon" />
          </div>
          <div>
            <p class="bcm-header__title">Billing Contacts</p>
            <p class="bcm-header__sub" v-if="exportState.billingContactsModalContext.value">
              {{ exportState.billingContactsModalContext.value.tpa_name || 'TPA' }}
              <span v-if="exportState.billingContactsModalContext.value.underwriter_name">
                · {{ exportState.billingContactsModalContext.value.underwriter_name }}
              </span>
            </p>
          </div>
        </div>
        <ul class="bcm-list">
          <li v-for="(email, idx) in exportState.billingContactsModalEmails.value" :key="idx" class="bcm-list__item">
            <b-icon icon="envelope" class="bcm-list__icon" />
            {{ email }}
          </li>
        </ul>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { defineComponent, ref, computed, nextTick, onUpdated, getCurrentInstance } from '@vue/composition-api'
import { useRoute, useRouter } from 'vue2-helpers/vue-router'

import InvoicingService from '@services/InvoicingService'
import { BaseDataTable } from '@components/base'
import { enableColumnResize } from '@components/base/base-data-table/useResizableColumns'
import { useCurrentUser } from '@composables'

import { useInvoiceExport } from './useInvoiceExport'
import {
  getApprovedDispatchedTableColumns,
  APPROVED_LABEL,
  DISPATCHED_STATUS_LABELS,
  CHANGED_AFTER_APPROVE_LABEL,
} from './Generateinvoicetable.constants'

export default defineComponent({
  name: 'ApprovedDispatchedInvoicesTable',

  components: {
    BaseDataTable,
  },

  props: {
    // 'needs-attention' | 'changed-after-approve' | null — set via the
    // header warning icon / the page's "changed since approval" banner.
    // Copied into local state so "Clear filter" can drop it without
    // needing a .sync round-trip through two parent levels.
    filterMode: {
      type: String,
      default: null,
    },
  },

  emits: ['changed'],

  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const proxy = instance.proxy
    const route = useRoute()
    const router = useRouter()

    const exportState = useInvoiceExport()
    const { userEmail } = useCurrentUser()

    const ApprovedDispatchedTableColumns = computed(() => getApprovedDispatchedTableColumns())

    const invoices = ref([])
    const loadingInvoices = ref(false)
    const loadError = ref(null)
    const total = ref(0)

    const modelValue = ref([])
    const dataTableRef = ref(null)

    const activeFilterMode = ref(props.filterMode)
    const clearFilterMode = () => {
      activeFilterMode.value = null
      // Drop ?filter=... from the URL too — otherwise a reload, or the
      // route-query watcher in Invoicing.vue, would bring the filter right
      // back even though the banner/checkbox says it's cleared.
      const { filter, ...rest } = route.query
      router.replace({ query: rest }).catch(() => {})
      handleRefresh()
    }

    // 'dispatch' | 'return' | false — which bulk action is currently running.
    const actionInFlight = ref(false)
    // invoice_id currently being reapproved, or null — separate from
    // actionInFlight since Reapprove is a per-row action, not tied to
    // checkbox selection.
    const reapprovingInvoiceId = ref(null)

    const isDispatched = (item) => !!(item && item.dispatched_at)
    const isChangedAfterApprove = (item) => !!(item && item.changed_after_approve)

    const filterBannerLabel = computed(() => {
      switch (activeFilterMode.value) {
        case 'needs-attention': return 'Approved – Manual + Changed After Approval'
        case 'changed-after-approve': return 'Changed After Approval'
        default: return ''
      }
    })

    // Pre-dispatch, every invoice just shows as plain "Approved" — the
    // Draft table only offers one Approve action now (always dispatch_mode
    // 'auto'), so distinguishing Approved_Auto/Approved_Manual here isn't
    // meaningful anymore. Once actually dispatched, Auto vs Manual still
    // matters (it's how it was sent), so that distinction is kept.
    const statusLabel = (item) => {
      if (isChangedAfterApprove(item)) return CHANGED_AFTER_APPROVE_LABEL
      if (isDispatched(item)) return DISPATCHED_STATUS_LABELS[item && item.status] || (item && item.status) || '—'
      return APPROVED_LABEL
    }
    const statusBadgeClass = (item) => {
      if (isChangedAfterApprove(item)) return 'gi-status-badge--changed'
      return isDispatched(item) ? 'gi-status-badge--dispatched' : 'gi-status-badge--approved'
    }

    // Every row is selectable now (dispatched invoices can be selected for
    // PDF/XLS export too) — which bulk actions are available depends on
    // the selection's mix, not on whether a row can be checked at all (see
    // allSelectedUndispatched below).
    const isRowSelectable = () => true

    // ── Data loading ─────────────────────────────────────────────────────

    const loadInvoices = async () => {
      loadingInvoices.value = true
      loadError.value = null
      try {
        const resp = await InvoicingService.getApprovedInvoices()
        const inner = resp.data && resp.data.data
        const rows = inner && inner.data
        invoices.value = Array.isArray(rows) ? rows : []
      } catch (e) {
        loadError.value = 'Failed to load approved/dispatched invoices.'
        console.error(e)
      } finally {
        loadingInvoices.value = false
      }
    }

    // Every row here is a real, persisted Lincoln Invoice — invoice_id is
    // always present (unlike the Pending table's virtual draft groups).
    const withIds = (rows) => rows.map(d => ({ ...d, id: d.invoice_id }))

    const selectedInvoiceObjects = computed(() =>
      invoices.value.filter(d => modelValue.value.includes(d.invoice_id)))

    // Shows only the first line of a TPA address for the Address column —
    // same treatment as the Draft table's. Full text is still available
    // via the cell's title attribute (native tooltip on hover).
    const firstAddressLine = (address) => {
      if (!address) return ''
      const firstLine = address.split(/[,\n]/)[0].trim()
      return firstLine || address.trim()
    }

    // Truncates a long FileVine value with an ellipsis — same treatment as
    // the Draft table's FileVine column.
    const truncateFilevine = (value) => {
      if (!value) return ''
      return value.length > 18 ? `${value.slice(0, 18)}…` : value
    }

    // Manual Dispatch and Return to Draft both only make sense when every
    // selected invoice is still undispatched — selecting even one already-
    // dispatched invoice alongside them hides both actions (only PDF/XLS/
    // Clear remain). Lincoln's DispatchInvoiceView/ReturnToDraftInvoiceView
    // both allow either Approved_Auto or Approved_Manual here — status no
    // longer gates this on the frontend either, only dispatched_at does.
    const allSelectedUndispatched = computed(() =>
      selectedInvoiceObjects.value.length > 0 &&
      selectedInvoiceObjects.value.every(d => !isDispatched(d)))

    const itemsProvider = async (context) => {
      const { filter, filters, currentPage, perPage, sortBy, sortDesc } = context

      const textMatch = (value, filterStr) => {
        if (!filterStr) return true
        return (value || '').toString().toLowerCase().includes(String(filterStr).toLowerCase())
      }
      const numericMatch = (value, filterStr) => {
        if (!filterStr) return true
        if (value === null || value === undefined) return false
        return String(value).toLowerCase().includes(String(filterStr).toLowerCase())
      }
      // dispatched_at is a plain "YYYY-MM-DD" string (see Lincoln's
      // _serialize_invoice), so lexicographic comparison against the
      // range bounds (same format, via BaseDataTable's date-range filter)
      // sorts correctly without parsing.
      const dateRangeMatch = (value, fromStr, toStr) => {
        if (!fromStr && !toStr) return true
        if (!value) return false
        if (fromStr && value < fromStr) return false
        if (toStr && value > toStr) return false
        return true
      }

      const filterMap = {}
      if (filters && filters.length) {
        filters.forEach(f => { filterMap[f.key] = f.value })
      }

      let rows = withIds(invoices.value).filter(d => {
        // Set via the header warning icon / "changed since approval" banner
        // — an OR-across-columns condition the generic per-column AND'd
        // filter system below can't express, so it's applied separately.
        // "needs attention" = Approved-Manual invoices still awaiting
        // dispatch (about to auto-send with no human review) OR any
        // invoice flagged changed_after_approve, dispatched or not.
        if (activeFilterMode.value === 'needs-attention' &&
            !((d.status === 'Approved_Manual' && !isDispatched(d)) || isChangedAfterApprove(d))) return false
        if (activeFilterMode.value === 'changed-after-approve' && !isChangedAfterApprove(d)) return false

        if (!textMatch(d.invoice_reference, filterMap.invoice_ref)) return false
        if (!textMatch(d.billing_type || 'Market', filterMap.billing_type)) return false
        if (!textMatch(statusLabel(d), filterMap.status)) return false
        if (!textMatch(d.tpa_name, filterMap.tpa)) return false
        if (!textMatch(d.underwriter_name, filterMap.underwriter)) return false
        if (!textMatch(d.currency_type, filterMap.currency_type)) return false
        if (!numericMatch(d.amount, filterMap.amount)) return false
        if (!textMatch(exportState.billingContactList(d).join(' '), filterMap.billing_contact)) return false
        if (!textMatch(d.tpa_address, filterMap.tpa_address)) return false
        if (!textMatch(d.tpa_filevine_id, filterMap.filevine_id)) return false
        if (!dateRangeMatch(d.dispatched_at, filterMap.dispatched_at_gt, filterMap.dispatched_at_lt)) return false

        if (filter) {
          const haystack = [
            d.invoice_reference, d.billing_type || 'Market', statusLabel(d), d.tpa_name, d.underwriter_name,
            d.currency_type, exportState.billingContactList(d).join(' '), d.tpa_address, d.tpa_filevine_id, d.dispatched_at,
          ].map(v => (v || '').toString().toLowerCase()).join(' ')
          if (!haystack.includes(filter.toLowerCase())) return false
        }

        return true
      })

      const sortKeyMap = {
        invoice_ref: d => d.invoice_reference || '',
        billing_type: d => d.billing_type || 'Market',
        status: d => statusLabel(d),
        tpa: d => d.tpa_name || '',
        underwriter: d => d.underwriter_name || '',
        currency_type: d => d.currency_type || '',
        bdx_count: d => d.bdx_count || 0,
        amount: d => d.amount || 0,
        billing_contact: d => exportState.billingContactList(d).join(' '),
        tpa_address: d => d.tpa_address || '',
        filevine_id: d => d.tpa_filevine_id || '',
        dispatched_at: d => d.dispatched_at || '',
      }

      if (sortBy && sortKeyMap[sortBy]) {
        const getKey = sortKeyMap[sortBy]
        rows = [...rows].sort((a, b) => {
          const av = getKey(a)
          const bv = getKey(b)
          if (av < bv) return -1
          if (av > bv) return 1
          return 0
        })
        if (sortDesc) rows.reverse()
      }

      total.value = rows.length

      const start = (currentPage - 1) * perPage
      return rows.slice(start, start + perPage)
    }

    const handleRefresh = async () => {
      await nextTick()
      if (!dataTableRef.value) return
      dataTableRef.value.handleRefresh()
    }

    const clearSelection = () => {
      modelValue.value = []
    }

    // ── Bulk actions ─────────────────────────────────────────────────────
    // Lincoln only exposes single-invoice dispatch/return-to-draft
    // endpoints, so bulk selection is applied sequentially (not
    // Promise.all) per invoice — avoids hammering the endpoint and lets us
    // report partial failures per invoice.

    const runBulkAction = async (kind, actionFn, successVerb) => {
      const targets = selectedInvoiceObjects.value
      if (!targets.length) return

      actionInFlight.value = kind
      const failures = []

      for (const invoice of targets) {
        try {
          await actionFn(invoice)
        } catch (e) {
          console.error(`${successVerb} failed for invoice:`, invoice, e)
          failures.push(invoice)
        }
      }

      if (targets.length - failures.length > 0) {
        proxy.$bvToast && proxy.$bvToast.toast(
          `${targets.length - failures.length} invoice(s) ${successVerb}.`,
          { title: 'Success', variant: 'success', autoHideDelay: 3000 }
        )
      }
      if (failures.length) {
        proxy.$bvToast && proxy.$bvToast.toast(
          `${failures.length} invoice(s) failed to ${kind === 'dispatch' ? 'dispatch' : 'return to draft'}.`,
          { title: 'Partial failure', variant: 'warning', autoHideDelay: 5000 }
        )
      }

      clearSelection()
      await loadInvoices()
      await handleRefresh()
      actionInFlight.value = false
      emit('changed')
    }

    const dispatchSelected = () =>
      runBulkAction('dispatch', (invoice) => InvoicingService.dispatchInvoice({ invoice_id: invoice.invoice_id }), 'dispatched')

    const returnSelected = () =>
      runBulkAction('return', (invoice) => InvoicingService.returnToDraft({ invoice_id: invoice.invoice_id }), 'returned to Draft')

    // ── Export (always from the archived Azure blob, never rebuilt) ─────
    // One at a time rather than exportState.exportSelected's single-zip
    // flow — every approved invoice has its PDF/XLS archived right at
    // approval time, so this only ever downloads the stored file. No
    // client-side regeneration fallback: if nothing's archived (Lincoln/
    // Azure unreachable, or a pre-existing invoice from before this
    // feature shipped), that invoice is reported as a failure instead of
    // silently rebuilding a possibly-stale document.
    const downloadSelected = async (format) => {
      const targets = selectedInvoiceObjects.value
      if (!targets.length) return

      const failures = []
      for (const invoice of targets) {
        try {
          await exportState.downloadStoredDocument(invoice, format)
        } catch (e) {
          console.error('Download failed for invoice:', invoice, e)
          failures.push(invoice)
        }
      }

      if (failures.length) {
        proxy.$bvToast && proxy.$bvToast.toast(
          `${failures.length} of ${targets.length} invoice(s) have no archived ${format.toUpperCase()} to download.`,
          { title: 'Download failed', variant: 'warning', autoHideDelay: 5000 }
        )
      }
    }

    // ── Reapprove (clears changed_after_approve) ────────────────────────
    // A per-row action, not tied to checkbox selection — a changed invoice
    // is reviewed and reapproved one at a time.

    const reapproveOne = async (item) => {
      reapprovingInvoiceId.value = item.invoice_id
      try {
        await InvoicingService.reapproveInvoice({ invoice_id: item.invoice_id, approved_by: userEmail.value })
        proxy.$bvToast && proxy.$bvToast.toast(
          `${item.invoice_reference} reapproved.`,
          { title: 'Reapproved', variant: 'success', autoHideDelay: 3000 }
        )
        await loadInvoices()
        await handleRefresh()
        emit('changed')

        // Charges may have changed since the original approval (that's
        // what triggered changed_after_approve in the first place) —
        // regenerate and re-archive the PDF/XLS to match.
        await exportState.generateAndUploadDocuments(item)
      } catch (e) {
        console.error('Reapprove failed:', e)
        proxy.$bvToast && proxy.$bvToast.toast(
          'Failed to reapprove invoice. Please try again.',
          { title: 'Error', variant: 'danger', autoHideDelay: 4000 }
        )
      } finally {
        reapprovingInvoiceId.value = null
      }
    }

    loadInvoices()

    // Drag-to-resize column headers, same as the Invoicing tab's tables.
    // Re-run on every update since the table only exists once invoices
    // have loaded (v-else); already-wired headers are skipped.
    onUpdated(() => nextTick(() => enableColumnResize(instance.proxy.$el)))

    return {
      exportState,
      ApprovedDispatchedTableColumns,

      invoices,
      loadingInvoices,
      loadError,
      total,

      modelValue,
      dataTableRef,
      selectedInvoiceObjects,
      firstAddressLine,
      truncateFilevine,

      activeFilterMode,
      clearFilterMode,
      filterBannerLabel,

      actionInFlight,
      reapprovingInvoiceId,
      allSelectedUndispatched,
      statusLabel,
      statusBadgeClass,
      isRowSelectable,

      itemsProvider,
      handleRefresh,
      clearSelection,

      dispatchSelected,
      returnSelected,
      downloadSelected,
      reapproveOne,

      loadInvoices,
    }
  },
})
</script>

<style scoped>
.gi-root { padding: 0; }

.gi-root >>> .table {
  font-size: 11px;
  table-layout: fixed;
}
.gi-root >>> .table th:first-child,
.gi-root >>> .table td:first-child {
  width: 28px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
}
.gi-root >>> .table td > div {
  padding: 8px 10px!important;
}
.gi-root >>> .table td:first-child > div {
  padding: 0!important;
}
.gi-root >>> .table th,
.gi-root >>> .table td {
  font-size: 11px!important;
  white-space: normal;
  word-break: break-word;
  vertical-align: middle;
}
.gi-root >>> .table thead th {
  font-size: 10px!important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 8px 6px;
}
.gi-root >>> .table .custom-checkbox {
  margin: 0 !important;
  display: inline-flex;
  align-items: center;
}

.gi-table-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #8898aa; margin-bottom: 8px;
}

.gi-action-bar {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; flex-wrap: wrap; gap: 8px;
}
.gi-action-bar__count { font-size: 13px; font-weight: 600; color: #32325d; }
.gi-action-bar__buttons { display: flex; gap: 6px; flex-wrap: wrap; }

.gi-progress-bar {
  background: #fff; border: 1px solid #e9ecef; border-radius: 6px;
  padding: 10px 16px; margin-bottom: 12px;
}
.gi-progress-bar__label {
  font-size: 12px; color: #32325d; font-weight: 600; margin-bottom: 6px;
}
.gi-progress-bar__track {
  width: 100%; height: 8px; border-radius: 4px;
  background: #e9ecef; overflow: hidden;
}
.gi-progress-bar__fill {
  height: 100%; background: #1c4a50; border-radius: 4px;
  transition: width 0.2s ease;
}

.gi-filter-banner {
  background: #fff; border: 1px solid #e9ecef; border-radius: 6px;
  padding: 8px 16px; margin-bottom: 12px; font-size: 12px; color: #525f7f;
}
.gi-filter-banner a { margin-left: 8px; color: #1c4a50; font-weight: 600; }

.gi-ref { color: #1c4a50; font-size: 10px; font-family: monospace; }
.gi-num { display: inline-block; font-size: 11px; }
.gi-amount { font-weight: 600; color: #1c4a50; font-size: 11px; }
.gi-contact { font-size: 10px; color: #525f7f; white-space: nowrap; text-wrap: inherit; }

/* Address & FileVine cells only — keep the (already JS-truncated) value on
   a single line so the ellipsis sits right after the text instead of
   wrapping. Not applied to the billing-contact cell, which needs its
   "+N" more-link to stay visible. */
.gi-truncate-cell {
  display: inline-block; max-width: 100%; vertical-align: bottom;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.gi-contact-more {
  color: #1c4a50; font-weight: 600; margin-left: 4px;
  text-decoration: none; cursor: pointer;
}
.gi-contact-more:hover { text-decoration: underline; }
.gi-alert-flag { color: #e67e22; font-weight: 700; margin-left: 2px; font-size: 10px; }

.gi-status-badge {
  display: inline-block; padding: 1px 6px; border-radius: 10px;
  font-size: 9px; font-weight: 600; background: #e9ecef; color: #525f7f;
  white-space: nowrap;
}
.gi-status-badge--approved { background: #cfe2ff; color: #084298; }
.gi-status-badge--dispatched { background: #d1e7dd; color: #0f5132; }
.gi-status-badge--changed { background: #fff3cd; color: #856404; }

.gi-reapprove-btn { font-size: 10px; padding: 2px 8px; margin-left: 6px; min-height: 24px; }

.gi-loading, .gi-empty { padding: 40px; text-align: center; color: #8898aa; font-size: 13px; }
.gi-error { padding: 16px; text-align: center; color: #dc3545; font-size: 13px; }

.bcm-root { position: relative; padding: 4px 0 0; }
.bcm-close {
  position: absolute; top: -8px; right: -8px;
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: transparent; color: #8898aa; font-size: 1.4rem;
  cursor: pointer; padding: 0; line-height: 1;
  transition: background 0.15s, color 0.15s;
}
.bcm-close:hover { background: #f0f0f0; color: #32325d; }

.bcm-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 16px; }
.bcm-header__icon-wrap {
  display: flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 50%;
  background: #e8f4f0; flex-shrink: 0;
}
.bcm-header__icon { font-size: 1.2rem; color: #1c4a50; }
.bcm-header__title { font-size: 0.95rem; font-weight: 700; color: #32325d; margin: 0 0 2px; }
.bcm-header__sub { font-size: 0.8rem; color: #525f7f; margin: 0; }

.bcm-list { list-style: none; margin: 0; padding: 0; }
.bcm-list__item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: 6px; font-size: 13px; color: #32325d;
  border-bottom: 1px solid #f1f3f5;
}
.bcm-list__item:last-child { border-bottom: none; }
.bcm-list__icon { color: #1c4a50; font-size: 12px; flex-shrink: 0; }
</style>
